import { getMarketCode, getLangCode } from '@kissui/helpers/src/dataLayer'
import { MAX_PRODUCTS } from '../js/constants'
import { COVEO_ORGANIZATION_ID, COVEO_PLATFORM_URL } from '../js/coveo.config'
import { loadExternalScript } from '@kissui/helpers/src/utils'
import { COVEO_CLIENT_ORIGIN_SEARCH_API_FETCH, COVEO_MAX_RESPONSE_TIME } from '../constants.mjs'
import { getCoveoAccessToken, isB2B } from '@kissui/coveo-search/src/config'
import {
    fieldsToInclude,
    fieldsToIncludeB2B
} from '../../../../coveo-search/src/experimental/engine/shared/coveo-api-commons'

let CoveoHeadless
const language = getLangCode()
const country = getMarketCode()
const upperCaseCountry = country.toUpperCase()
const locale = `${language}-${upperCaseCountry}`
export const fetchProductsLastOrder = async (checkoutService = window.napi.checkout) => {
    try {
        const lastOrder = await checkoutService().getMyLastOrder()

        if (!lastOrder?.quotation?.cartLines?.length) {
            return []
        }

        return lastOrder.quotation.cartLines
            .map(cartItem => cartItem.legacyId)
            .slice(0, MAX_PRODUCTS)
    } catch (error) {
        console.error('Failed to retrieve last order data', error)
        throw new Error('Failed to retrieve last order data')
    }
}
const expectedRecommendations = [
    'VER_LIMITED_EDITION_1',
    'VER_LIMITED_EDITION_2',
    'VER_LIMITED_EDITION_3',
    'VER_LIMITED_EDITION_4',
    'VER_LIMITED_EDITION_5',
    'CLA_LIMITED_EDITION_1',
    'CLA_LIMITED_EDITION_2',
    'CLA_PERMANENT_5',
    'VER_PERMANENT_1',
    'VER_PERMANENT_2',
    'VER_PERMANENT_3',
    'VER_PERMANENT_4',
    'VER_PERMANENT_5'
]
/**
 * @returns {Promise<string[]>}
 */
export const fetchHQRecommendation = async () => {
    // This prevent the double module instantiation when using redirection tools, both localhost and nespresso.com
    const { subscribe } = await import(
        `${window.location.origin}/shared_res/agility/next-components/vendors/event-queue.es.min.js`
    )

    const baseUrl = `/ecapi/products/v2/${country}/b2c/productsByCategories`
    const params = new URLSearchParams({
        language,
        superCategory: 'capsules',
        allDetails: 'false',
        usageIntent: 'standard-order'
    })
    const url = `${baseUrl}?${params}`

    const plpProductList = []

    const response = await fetch(url)

    if (!response.ok) {
        throw new Error('Failed to retrieve products by categories data')
    }
    const data = await response.json()

    data.forEach(category => {
        category?.products?.forEach(product => {
            plpProductList.push(product.legacyId)
        })
    })

    const waitTimeout = 10000
    let timeoutHandler
    let unsubscribe
    return new Promise((resolve, reject) => {
        timeoutHandler = setTimeout(
            reject,
            waitTimeout,
            new Error(`"hq-products-recommendation" message not received in ${waitTimeout}ms`)
        )
        unsubscribe = subscribe('hq-products-recommendation', ({ message }) => {
            if (
                typeof message !== 'object' ||
                Array.isArray(message) ||
                expectedRecommendations.some(key => !(key in message))
            ) {
                return reject(
                    new TypeError(
                        `"hq-products-recommendation" message has unexpected format ${JSON.stringify(
                            message
                        )}`
                    )
                )
            }
            const arrayOfSKUs = Object.values(message).filter(Boolean)
            //checking the ECAPI list here to make sure if they are present in it
            const filteredArrayOfSKUs = arrayOfSKUs.filter(sku => plpProductList.includes(sku))
            return resolve(filteredArrayOfSKUs)
        })
    }).finally(() => {
        clearTimeout(timeoutHandler)
        unsubscribe()
    })
}

export const fetchCoveo = async (searchHub, skus) => {
    validateCoveoHeadless()

    const selectedCountry = getSelectedCountry(country)
    const website = getWebsite(country)

    const engine = CoveoHeadless.buildSearchEngine({
        configuration: {
            organizationId: COVEO_ORGANIZATION_ID,
            accessToken: getCoveoAccessToken(),
            platformUrl: COVEO_PLATFORM_URL,
            search: {
                searchHub: getSearchHubName(searchHub),
                pipeline: 'Product Recommendations'
            },
            preprocessRequest: (request, clientOrigin) => {
                if (clientOrigin === COVEO_CLIENT_ORIGIN_SEARCH_API_FETCH) {
                    const body = JSON.parse(request.body.toString())
                    body.context = {
                        website: website,
                        sitename: website,
                        context_website: website,
                        context_sitename: website,
                        language,
                        locale,
                        country: selectedCountry
                    }
                    body.mlParameters = { itemId: '8899.81' }
                    assignSkusToMlParameters(skus, body)
                    body.recommendation = 'Recommendation'
                    body.fieldsToInclude = getFieldsToInclude()
                    body.numberOfResults = MAX_PRODUCTS
                    request.body = JSON.stringify(body)
                }
                return request
            }
        }
    })

    const recsList = CoveoHeadless.buildResultList(engine)

    return new Promise((resolve, reject) => {
        const unsubscribe = recsList.subscribe(() => {
            const state = recsList.state
            if (!state.isLoading && state.firstSearchExecuted) {
                const results = state.results
                resolve(results.map(result => result.raw.nes_prd_sku))
            }
        })

        window.setTimeout(() => {
            unsubscribe()
            reject(new Error('Error Coveo - Request timed out'))
        }, COVEO_MAX_RESPONSE_TIME)
        engine.executeFirstSearch()
    })
}

export async function init() {
    return new Promise((resolve, reject) => {
        loadExternalScript({
            src: 'https://unpkg.com/@coveo/headless@2.0.1/dist/browser/headless.js',
            defer: true,
            callback: () => {
                if (!window.CoveoHeadless) {
                    return reject(new Error('Coveo Headless failed to load'))
                }
                CoveoHeadless = window.CoveoHeadless
                resolve(CoveoHeadless)
            },
            error: error => reject(error)
        })
    })
}

export function assignSkusToMlParameters(skus, body) {
    if (skus && skus.length) {
        if (skus.length > 1) {
            body.mlParameters = { itemIds: skus }
        } else {
            body.mlParameters = { itemId: skus[0] }
        }
    }
}

export function getFieldsToInclude() {
    return isB2B() ? fieldsToIncludeB2B : fieldsToInclude
}

export function getSearchHubName(searchHub) {
    return isB2B() ? `Nespresso_PRO_${country.toUpperCase()}_Search` : searchHub
}

export function getCountryForCoveoB2B(country) {
    return `pro_${country}`
}

export function getSelectedCountry(country) {
    return isB2B() ? getCountryForCoveoB2B(country) : country
}

export function getUpperCaseCountryForCoveoB2B(country) {
    return getCountryForCoveoB2B(country).toUpperCase()
}

export function getWebsite(country) {
    const upperCaseCountry = country.toUpperCase()
    return isB2B()
        ? `Nespresso_${getUpperCaseCountryForCoveoB2B(country)}`
        : `Nespresso_${upperCaseCountry}`
}

export function validateCoveoHeadless() {
    if (!CoveoHeadless) {
        throw new Error('fetchCoveo was called before init')
    }
}

export default {
    init,
    fetchProductsLastOrder,
    fetchCoveo,
    fetchHQRecommendation,
    assignSkusToMlParameters,
    getFieldsToInclude,
    getSearchHubName,
    getCountryForCoveoB2B,
    getSelectedCountry,
    getUpperCaseCountryForCoveoB2B,
    getWebsite,
    validateCoveoHeadless
}
